import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { supabase } from "@/lib/supabaseClient";
import type { Database } from "@/types/database.types";
import type { User, AuthChangeEvent, Session } from "@supabase/supabase-js";

type ProfileRow = Database["public"]["Tables"]["profiles"]["Row"];

interface AuthContextType {
  user: User | null;
  profile: ProfileRow | null;
  loading: boolean;
  error: Error | null;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error: fetchError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      setProfile(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to fetch profile");
      setError(error);
      console.error("Error fetching profile:", error);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  }, [user, fetchProfile]);

  useEffect(() => {
    let isInitialized = false;

    // Get initial session
    const initAuth = async () => {
      console.log("[AuthContext] initAuth: START");
      try {
        const {
          data: { user: currentUser },
        } = await supabase.auth.getUser();
        console.log("[AuthContext] initAuth: getUser result", { userId: currentUser?.id });

        setUser(currentUser);

        if (currentUser) {
          console.log("[AuthContext] initAuth: fetching profile for", currentUser.id);
          await fetchProfile(currentUser.id);
        }
      } catch (err) {
        console.error("Error initializing auth:", err);
      } finally {
        isInitialized = true;
        console.log("[AuthContext] initAuth: DONE, isInitialized=true, setting loading=false");
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
      const newUser = session?.user ?? null;
      console.log("[AuthContext] onAuthStateChange:", { event, userId: newUser?.id, isInitialized });
      setUser(newUser);

      if (event === "SIGNED_OUT") {
        console.log("[AuthContext] onAuthStateChange: SIGNED_OUT, clearing profile and error");
        setProfile(null);
        setError(null);
      } else if (newUser && (event === "SIGNED_IN" || event === "TOKEN_REFRESHED")) {
        console.log("[AuthContext] onAuthStateChange: SIGNED_IN or TOKEN_REFRESHED, userId=", newUser.id);
        if (isInitialized) {
          console.log("[AuthContext] onAuthStateChange: fetching profile (isInitialized=true)");
          void fetchProfile(newUser.id);
        } else {
          console.log("[AuthContext] onAuthStateChange: skipping profile fetch (isInitialized=false, initAuth will handle)");
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  return (
    <AuthContext.Provider value={{ user, profile, loading, error, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}